import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { constant } from 'app/app.constants';
import { SweetAlertHelper } from 'app/core/helpers/sweet-alert-helper.service';
import { ToastrAlertHelper } from 'app/core/helpers/toastr-alert-helper-service';
import { PostingService } from 'app/core/services/posting.service';
import { UploadsService } from 'app/pages/A2-utils/uploads.service';
import { T2LoadingService } from 'app/trip2-theme/components/t2-loading/t2-loading.service';
import { Subscription } from 'rxjs/Subscription';
import { creditTypesEnum } from '../posting.component';

@Component({
  selector: 'app-import-posting-batch',
  templateUrl: './import-posting-batch.component.html',
  styleUrls: ['./import-posting-batch.component.scss'],
})

export class ImportPostingBatchComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  formImportPosting: FormGroup;
  arrayCreditPayments: any[] = [];
  arrayCreditOffSets: any[] = [];
  arrayCreditContractual: any[] = [];
  templateUrl = 'https://webapp-trip2-cms.s3.amazonaws.com/Import+Electronic+Remmitance+Spreadsheet.xlsx';

  get importcontractualCtrl(): AbstractControl {
    return this.formImportPosting.get('importcontractual');
  }

  get contractualallowanceCtrl(): AbstractControl {
    return this.formImportPosting.get('contractualallowance');
  }

  get importMandatedCtrl(): AbstractControl {
    return this.formImportPosting.get('importMandated');
  }

  get importCommercialCtrl(): AbstractControl {
    return this.formImportPosting.get('importCommercial');
  }

  get filesNamesCtrl(): AbstractControl {
    return this.formImportPosting.get('filesnames');
  }

  constructor(
    private fb: FormBuilder,
    private postingService: PostingService,
    private sweetAlertHelper: SweetAlertHelper,
    private uploadsService: UploadsService,
    private toastrAlertHelper: ToastrAlertHelper,
    private t2LoadingService: T2LoadingService,
  ) {
    this.createForm();
    this.subscribeFormChanges();
  }

  ngOnInit() {
    this.loadCreditPayments();
    this.loadCreditOffSets();
    this.loadCreditContractual();
  }

  loadCreditPayments() {
    this.subscriptions.push(
      this.postingService.getCreditTypeList(1).subscribe(
        (resp: any) => {
          if (resp) {
            this.arrayCreditPayments = resp;
          }
        },
        (error) => {
          this.sweetAlertHelper.createCustomAlert({
            title: 'Error',
            text: error.message,
            type: 'error',
          });
        }
      )
    );
  }

  loadCreditOffSets() {
    this.subscriptions.push(
      this.postingService.getCreditTypeList(4).subscribe(
        (resp: any) => {
          if (resp) {
            this.arrayCreditOffSets = resp;
          }
        },
        (error) => {
          this.sweetAlertHelper.createCustomAlert({
            title: 'Error',
            text: error.message,
            type: 'error',
          });
        }
      )
    );
  }

  loadCreditContractual() {
    this.subscriptions.push(
      this.postingService.getCreditTypeList(creditTypesEnum.contractual, true).subscribe(
        (resp: any) => {
          if (resp) {
            this.arrayCreditContractual = resp;
          }
        },
        (error) => {
          this.sweetAlertHelper.createCustomAlert({
            title: 'Error',
            text: error.message,
            type: 'error',
          });
        }
      )
    );
  }

  subscribeFormChanges() {
    this.formImportPosting
      .get('importcontractual')
      .valueChanges.subscribe((value: boolean) => {
        if (value) {
          this.formImportPosting
            .get('contractualallowance')
            .setValidators([Validators.required]);
          this.formImportPosting.updateValueAndValidity();
        } else {
          this.formImportPosting.get('contractualallowance').clearValidators();
          this.formImportPosting.updateValueAndValidity();
          this.formImportPosting.patchValue({
            contractualallowance: null,
          });
        }
      });

    this.formImportPosting.get('isansi').valueChanges.subscribe((value: string) => {
      if (value === '1') {
        this.formImportPosting.get('isspreadsheet').setValue(null);
        this.formImportPosting.updateValueAndValidity();
      }
    });

    this.formImportPosting.get('isspreadsheet').valueChanges.subscribe((value: string) => {
      if (value === '1') {
        this.formImportPosting.get('isansi').setValue(null);
        this.formImportPosting.updateValueAndValidity();
      }
    });
  }

  createForm() {
    this.formImportPosting = this.fb.group({
      importcontractual: [false],
      credittoapplypayments: [null, Validators.required],
      credittoapplyoffsets: [null, Validators.required],
      filesnames: ['', Validators.required],
      uploadfiles: ['', Validators.required],
      contractualallowance: [null],
      importMandated: [false],
      importCommercial: [false],
      remark: false,
      isansi: '1',
      isspreadsheet: null,
    });
  }

  get showImportContractual(): boolean {
    return this.formImportPosting.get('importcontractual').value;
  }

  get showFileInput(): string {
    return this.formImportPosting.get('isansi').value === '1' ? '.era' : '.xls, .xlsx, .csv';
  }

  selectedFiles(event: any) {
    if (
      event &&
      event.target &&
      event.target.files &&
      event.target.files.length
    ) {
      let texto = '';
      for (const name of event.target.files) {
        texto += name.name + ', ';
      }
      this.formImportPosting.patchValue({
        filesnames: texto,
        uploadfiles: event.target.files,
      });
    }
  }

  save(): void {
    const formData = Object.assign({}, this.formImportPosting.value);
    if (this.formImportPosting.valid) {
      this.sweetAlertHelper
        .createCustomAlert({
          title: 'Process File',
          text: 'Are you sure you want to process these remittance files?',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, import it!',
          cancelButtonText: 'No, cancel!',
        })
        .then((result) => {
          const type = formData.isansi === '1' ? 'importElectronic' : 'importExcelFile';
          const url = `${constant.URL_POSTING}/${type}`;

          if (result.value) {
            this.toastrAlertHelper.info('Importing file');
            if (formData.isansi === '1') {
              this.uploadAnsi(url, formData);
            } else {
              this.uploadSpraedsheet(url, formData);
            }
          }
        });
    } else {
      this.sweetAlertHelper
        .createCustomAlert({
          title: 'Error',
          text: 'Please fill all required fields',
          type: 'error',
        })
        .then(() => {
          this.formImportPosting.touched;
        });
    }
  }

  uploadAnsi(url: string, formData: any): void {
    this.t2LoadingService.show();
    const offsets = formData.credittoapplyoffsets ? formData.credittoapplyoffsets : 0;
    const payments = formData.credittoapplypayments ? formData.credittoapplypayments : 0;

    const body: ImportEraCreditTypeI = {
      CreditToApplyOffsets: offsets,
      CreditToApplyPayments: payments,
      CreditToApplyContractualAllowance: 0,
      MandatedContractual: false,
      CommercialContractual: false
    };

    if (this.showImportContractual) {
      body.CreditToApplyContractualAllowance = this.contractualallowanceCtrl.value;
    }
    if (this.importMandatedCtrl.value) { body.MandatedContractual = true; }
    if (this.importCommercialCtrl.value) { body.CommercialContractual = true; }

    this.subscriptions.push(
      this.uploadsService
        .makeFileRequest(url, [{ nameParam: 'dataBodyEra', data: body }], formData.uploadfiles)
        .subscribe(
          (resp: any) => {
            if (resp && resp.length) {
              this.t2LoadingService.hiden();
              let messageUpdate = '';
              resp.forEach((file: any) => {
                if (file) {
                  messageUpdate += `
                    <div style="border-bottom: 1px solid rgb(205, 205, 205);" >
                      File ${file.filename}
                      ${file.insertedchecks > 0 ? 'was processed successfully!' :
                                                  '<span style="color: red;">Check is already processed.</span>'}.
                      <br /> <span style="font-weight: bold;">(Inserted Checks: ${file.insertedchecks})</span> <br />`;

                      if (file.checknumberlist.length) {
                        file.checknumberlist.forEach((row: any, index: number) => {
                          messageUpdate += `<span style="font-weight: bold;">${index + 1} - Check N°: [${row.checknumber}]</span>`;

                          if (row.payors.length) {
                            messageUpdate += `<br />
                            <div>Payor list</div>
                            <div class="scrollbar scrollbar-small" style="margin-bottom: 1px;">
                              <table style="width: 100%;">
                                <tbody>`;
                                  row.payors.forEach((ro: any) => {
                                    messageUpdate += `
                                    <tr>
                                      <td>${ro.payorid} - ${ro.description}</td>
                                    </tr>`;
                                  });
                              messageUpdate += `
                                </tbody>
                              </table>
                            </div>`;
                          }

                        });
                      }
                  messageUpdate += `</div>`;
                }
              });

              this.sweetAlertHelper
              .createCustomAlert({
                title: 'Process File',
                html: `
                    <div>
                      <p>${messageUpdate}</p>
                    </div>
                `,
                type: 'warning',
              })
              .then(() => {
                this.formImportPosting.reset();
                this.postingService.reloadMainGrid(true);
              });
            }
          },
          (error) => {
            this.t2LoadingService.hiden();
            this.postingService.reloadMainGrid(true);
            this.sweetAlertHelper.createCustomAlert({
              title: 'Error',
              text: error.message,
              type: 'error',
            });
          }
        )
    );
  }

  uploadSpraedsheet(url: string, formData: any) {
    this.t2LoadingService.show();
    const offsets = formData.credittoapplyoffsets ? formData.credittoapplyoffsets : 0;
    const payments = formData.credittoapplypayments ? formData.credittoapplypayments : 0;

    const body: ImportEraCreditTypeI = {
      CreditToApplyOffsets: offsets,
      CreditToApplyPayments: payments,
      CreditToApplyContractualAllowance: 0,
      MandatedContractual: false,
      CommercialContractual: false
    };

    this.subscriptions.push(
      this.uploadsService
        .makeFileRequest(url, [{ nameParam: 'dataBodySpreadSheet', data: body }], formData.uploadfiles)
        .subscribe(
          (resp: any) => {
            if (resp && resp.length) {
              this.t2LoadingService.hiden();

              let typeSwal: any = 'success';

              const inserted = resp.map((row: any) => row.insertedrows);
              if (inserted.reduce((prev: number, current: number) => prev + current) === 0) { typeSwal = 'error'; }

              let newLayout = '';

              resp.forEach((level1: any) => {

                newLayout +=
                  `${level1.insertedrows > 0 ? `<div><b>Check N°: ${level1.check}.</b>. Success: ${level1.insertedrows}</div>` : ``}`;

                if (level1.rowexceptions && level1.rowexceptions.length) {
                  newLayout += `
                  <div style="color: red;">
                    ${level1.rowexceptions.length ? `${level1.insertedrows !== 0 ? `<b>Check N°: </b> ${level1.check}.` : '' }
                    <b> Exceptions ${level1.filetype} </b> ${level1.rowexceptions.length}.` : ''}
                  </div>

                  <div style="color: red; overflow: auto; min-height: 5px; max-height: 150px;
                  border-bottom: 1px solid #ccc; padding-bottom: 5px; margin-bottom: 5px;">`;

                  level1.rowexceptions.forEach((level2: any, index: number) => {
                    newLayout +=
                      `<p>
                        <b>${index + 1} - </b>
                        ${level2.dos ? `<b>DOS:</b> ${level2.dos}.` : ''}
                        ${level2.runnumber ? `<b>Consecutive: </b> ${level2.runnumber}.</br>` : ''}
                        <b>Error: </b> ${level2.errormessage}
                      </p></br>`;
                  });

                  newLayout += `</div>`;
                }
              });

              this.sweetAlertHelper.createCustomAlert({
                title: 'Import File',
                html: newLayout,
                type: typeSwal
              })
              .then(() => {
                this.formImportPosting.reset();
                this.postingService.reloadMainGrid(true);
              });
            }
          },
          (error) => {
            this.t2LoadingService.hiden();
            this.sweetAlertHelper.createCustomAlert({
              title: 'Error',
              text: error.message,
              type: 'error',
            });
          }
        )
    );
  }

  get isSpreadsheet(): boolean {
    return this.formImportPosting.get('isansi').value === '1';
  }

  cancel() {
    this.postingService.reloadMainGrid(false);
    this.postingService.setBatchCreditListSubject(null);
  }

  fileChanged(): void {
    this.filesNamesCtrl.patchValue('');
    if (!this.isSpreadsheet) {
      this.importcontractualCtrl.patchValue(false);
      this.contractualallowanceCtrl.patchValue(null);
      this.importMandatedCtrl.patchValue(false);
      this.importCommercialCtrl.patchValue(false);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}

export interface ImportEraCreditTypeI {
  CreditToApplyPayments: number;
  CreditToApplyOffsets: number;
  CreditToApplyContractualAllowance: number;
  MandatedContractual: boolean;
  CommercialContractual: boolean;
}



// WEBPACK FOOTER //
// ./src/app/pages/billing-general/posting/import-posting-batch/import-posting-batch.component.ts